import { makeAutoObservable } from 'mobx';

/**
 * Whether an in-store QR link is still being resolved into a pickup address.
 *
 * Read by the mandatory address gate, which would otherwise open the address
 * modal over a link that is about to make it unnecessary — the one screen the
 * whole feature exists to skip.
 *
 * Deliberately its own module, with no imports beyond mobx. `mandatoryAddress`
 * is pulled in by the lazily loaded address modal as well as by App, and one
 * boolean is no reason for that chunk to carry delivery zones, address saving
 * and analytics. App still reaches the link's own module through the hook —
 * splitting keeps the flag cheap to read, it does not make the feature lazy.
 *
 * A module singleton, so it must only ever be written from the client — the SSR
 * server shares it across every request.
 */
class PickupStoreLinkState {
  /**
   * A link is still resolving, so nothing may ask the customer for an address.
   * Lasts the whole flow, including the save, which has no bound of its own.
   */
  isPending = false;
  /**
   * The storefront is held back because painting it would show the assortment
   * of the wrong warehouse. Released on a budget, separately from `isPending`:
   * once the address and the ETA are known, the remaining save can finish
   * behind a visible page — the modal does exactly that — and a blank screen
   * with no ceiling is worse than a brief wrong assortment.
   */
  isBlocking = false;

  constructor() {
    makeAutoObservable(this);
  }

  setPending(value: boolean) {
    this.isPending = value;
    if (!value) {
      this.isBlocking = false;
    }
  }

  setBlocking(value: boolean) {
    this.isBlocking = value;
  }
}

export const pickupStoreLinkState = new PickupStoreLinkState();
